import {
  Ban,
  CheckCircle2,
  Eye,
  FileEdit,
  Flag,
  RotateCcw,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

import {
  PROJECT_STATUS_LABELS,
  PROJECT_STATUS_TONES,
  type ProjectStatus,
  type StatusTone,
} from '@/shared/constants/statuses';
import { cn } from '@/shared/lib/utils';

/**
 * Situação do projeto na esteira.
 *
 * A distinção entre "aguardando decisão" e "confirmado" é requisito de produto
 * (US-14), não decoração — e ela não pode depender só de cor: o rótulo em
 * texto vai junto, sempre.
 */

/**
 * Só variantes claras: o painel é claro (ver `src/styles/index.css`). As
 * variantes `dark:` que existiam aqui saíram junto com o bloco `.dark` — como
 * nada aplica a classe, elas eram CSS que nunca chegaria à tela e uma segunda
 * lista de cores para manter em sincronia sem ninguém perceber quando ela
 * divergisse.
 */
/**
 * `info` é CIANO, e não o `sky` que era — a troca é consequência direta do azul
 * da igreja ter virado `--primary` (ver `styles/index.css`).
 *
 * Na listagem de projetos, a coluna de crachás fica cercada de azul da marca:
 * item ativo da navegação, botão "Novo projeto" e links. Com o índigo antigo o
 * `sky` ficava a 44° disso; com o azul da igreja ficaria a 23°, e um crachá
 * azul-claro nesse meio para de ser lido como SITUAÇÃO e passa a ser lido como
 * mais um elemento do sistema. O ciano abre a distância para 33° sem mudar o
 * que o tom significa: continua sendo a cor fria de "está em trânsito".
 */
const TONE_CLASSES: Record<StatusTone, string> = {
  neutral: 'bg-slate-100 text-slate-700 ring-slate-200',
  info: 'bg-cyan-50 text-cyan-800 ring-cyan-200',
  warning: 'bg-amber-50 text-amber-900 ring-amber-200',
  success: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  danger: 'bg-rose-50 text-rose-800 ring-rose-200',
};

/**
 * Um ícone por situação, indexado pelo STATUS e não pelo rótulo.
 *
 * O ícone não substitui o texto — ele dá ao crachá uma silhueta reconhecível
 * antes da leitura, que é o que permite varrer uma coluna de cartões sem ler
 * palavra por palavra. Por isso as sete formas são deliberadamente distintas
 * entre si; dois ícones parecidos custariam mais do que ícone nenhum. E é
 * também por isso que ele é sempre `aria-hidden`: para leitor de tela o crachá
 * já diz tudo em português.
 *
 * ⚠️ **O crachá diz ONDE o projeto está; quem diz O QUE aconteceu é a linha de
 * atribuição** do cabeçalho (`features/projects/lib/attribution.ts`). É a regra
 * que substituiu o par `Send`/`Eye` daqui: o avião existia para `submitted`, e
 * "Enviado" não era situação — era um ato, que o líder infere sem crachá
 * nenhum, porque quem enviou sabe que enviou (ADR-013). Sobrou o olho, que
 * responde a pergunta certa: alguém está olhando. `Eye`, e não um tique de
 * prancheta — tique promete conclusão numa etapa que ainda não decidiu nada.
 *
 * `CheckCircle2` fica reservado a `approved`: é a única confirmação da esteira.
 * `completed` recebe a bandeira de chegada, e não um segundo tique, senão os
 * dois estados verdes/finais viram a mesma mancha na lista.
 */
const STATUS_ICONS: Record<ProjectStatus, LucideIcon> = {
  draft: FileEdit,
  under_review: Eye,
  changes_requested: RotateCcw,
  approved: CheckCircle2,
  rejected: XCircle,
  cancelled: Ban,
  completed: Flag,
};

interface StatusPillProps {
  label: string;
  tone: StatusTone;
  icon: LucideIcon;
  className?: string;
}

/**
 * A forma do crachá, sem opinião sobre o que ele diz.
 *
 * Existe porque o painel tem UMA leitura que não é situação da esteira —
 * "Aguardando relatório", `approved` visto depois da data (decisão 29) — e ela
 * precisa da mesma silhueta das outras oito para ser lida no mesmo lugar da
 * linha. Quem a compõe é `features/projects/ProjectStatusBadge`: `shared/` não
 * pode conhecer `features/`, e a regra do relógio é da feature.
 */
export function StatusPill({ label, tone, icon: Icon, className }: StatusPillProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full py-0.5 pl-2 pr-2.5 text-xs font-medium ring-1 ring-inset',
        TONE_CLASSES[tone],
        className,
      )}
    >
      {/* 0.8125rem, e não `size-3.5`: o ícone tem de ficar ABAIXO da altura da
          maiúscula do rótulo. Do tamanho dela ele compete com o texto; um
          pouco menor, ele ancora. */}
      <Icon aria-hidden="true" className="size-[0.8125rem] shrink-0" />
      {label}
    </span>
  );
}

interface StatusBadgeProps {
  status: ProjectStatus;
  className?: string;
}

export function StatusBadge({ status, className }: StatusBadgeProps) {
  return (
    <StatusPill
      label={PROJECT_STATUS_LABELS[status]}
      tone={PROJECT_STATUS_TONES[status]}
      icon={STATUS_ICONS[status]}
      className={className}
    />
  );
}
